import type {
  Building,
  Core,
  HumanCommandSource,
  MatchState,
  PermanentBuildingKind,
  PlayerId,
  ProductionHub,
  Squad,
  SquadArchetype,
} from '@signalclash/shared';
import {
  BUILDINGS,
  DOCTRINE_DEFS,
  OVERTIME_DURATION_SEC,
  PERMANENT_BUILDING_KINDS,
  SQUAD_DEFS,
  UNDERDOG_SURGE_BONUS,
  isOperational,
} from '@signalclash/shared';

import type { BuildMode } from '../input/BuildMode.js';
import type { InputRouter } from '../input/InputRouter.js';
import type { Selection, SelectionRef } from '../input/Selection.js';

import { injectCommandCardStyles } from './hudCommandCardStyles.js';
import { injectHudCoherenceStyles } from './hudCoherenceStyles.js';
import { installHudTooltips } from './HudTooltip.js';
import type { BuildButton, CmdCell, CmdKey, ProdButton } from './hudHelpers.js';
import {
  ARCHETYPE_BUILD_TOOLTIP,
  ARCHETYPE_GLYPH,
  ARCHETYPE_TAGLINE,
  BUILD_KIND_EFFECT,
  BUILD_KIND_GLYPH,
  BUILD_KIND_TOOLTIP,
  BUILD_PANEL_FOOTER_HINT,
  CMD_CELL_SPECS,
  applyCmd,
  capitalize,
  computeBuildKindStates,
  computeSquadCmdStates,
  ensureDiv,
  formatClock,
  labelForBuildKind,
  makeCmdCell,
  makeStat,
} from './hudHelpers.js';
import { injectHudStyles } from './hudStyles.js';

const PRODUCIBLE: SquadArchetype[] = ['recon', 'assault', 'engineer', 'shield'];
const TIMER_FLASH_THRESHOLD_SEC = 60;
// Per docs/prompts/04_production_buildings.md "Pacing copy in HUD: ... add
// 'Dominance Check in 0:30' banner at 7:30."
const DOMINANCE_BANNER_LEAD_SEC = 30;

// HUD owns four in-match regions per docs/09_UI_UX.md V0.4 spec:
//   - Top-left: resource line (Signal · income/sec · squads X / cap).
//   - Top-center: match timer; turns red and flashes under TIMER_FLASH_THRESHOLD_SEC.
//   - Top-center (below timer): "Dominance Check in 0:30" banner from 7:30.
//   - Bottom-right: contextual COMMAND CARD (selected squad / Core / Hub).
//
// V4 — the bottom-right context panel is now a true RTS Command Card:
// portrait + clickable command grid (Attack-Move / Stop / Q-ability /
// E-ability) for squads; richer icon-tile production buttons + an embedded
// Build row (Hub / Turret) for own Cores / Hubs. Buttons drive the same
// InputRouter trigger* hooks the keyboard shortcuts use so desktop and
// mobile share one source of truth and keyboard parity is preserved.
//
// Reads sim snapshots; emits intents through HumanCommandSource and the
// InputRouter / BuildMode public APIs. Holds NO gameplay state. The
// hide()/show() pair is used by ClientApp around scene transitions so the
// HUD doesn't bleed into the MainMenu screen.
export class HudPanel {
  private readonly container: HTMLDivElement;
  private readonly resourceEl: HTMLElement;
  private readonly resourceSignalValue: HTMLElement;
  private readonly resourceIncomeValue: HTMLElement;
  private readonly resourceSquadsValue: HTMLElement;
  private readonly signalBarFill: HTMLElement;
  private readonly timerEl: HTMLElement;
  private readonly bannerEl: HTMLElement;
  private readonly surgeIndicator: HTMLElement;
  private readonly doctrinePill: HTMLElement;
  private readonly enemyPanel: HTMLElement;
  private readonly enemyPanelTitle: HTMLElement;
  private readonly enemySurgeIndicator: HTMLElement;
  private readonly enemyDoctrinePill: HTMLElement;
  private readonly overtimeBanner: HTMLElement;
  private readonly contextEl: HTMLElement;
  private readonly emptyEl: HTMLElement;
  // V5+ — empty-state widgets. emptyHint is the original "Select a squad…"
  // text; selectAllBtn is the mobile-RTS "Select All Squads (n)" CTA that
  // takes over when the player has own squads but nothing selected.
  private readonly emptyHint: HTMLElement;
  private readonly selectAllBtn: HTMLButtonElement;
  private readonly squadPanel: HTMLElement;
  private readonly squadPortrait: HTMLElement;
  private readonly squadName: HTMLElement;
  // Role tagline beneath the squad name — explains the archetype's purpose
  // (e.g., "Scout & paint targets") so new players don't have to read stats.
  private readonly squadTagline: HTMLElement;
  private readonly squadHpFill: HTMLElement;
  private readonly squadHpText: HTMLElement;
  // V4 command grid — single grid of icon-first buttons replacing the
  // display-only ability rows. Each cell is keyed by an opaque id (see
  // makeCmdCell) and renderCmd() reads cooldown / armed-state per frame.
  private readonly cmdGrid: HTMLElement;
  private readonly cmdCells = new Map<CmdKey, CmdCell>();
  private readonly corePanel: HTMLElement;
  private readonly coreSignal: HTMLElement;
  private readonly coreSquads: HTMLElement;
  private readonly coreTitle: HTMLElement;
  private readonly queueText: HTMLElement;
  private readonly queueBarFill: HTMLElement;
  private readonly buttons = new Map<SquadArchetype, ProdButton>();
  // Build row inside the Core/Hub command card — the producing structure IS
  // the builder, so its command card surfaces the build actions instead of
  // the always-on bottom-center BuildPanel.
  private readonly buildSection: HTMLElement;
  private readonly buildButtons = new Map<PermanentBuildingKind, BuildButton>();
  private latestState: MatchState | null = null;
  private latestSelection: SelectionRef | null = null;
  // Mutable: in network mode the server assigns the slot in `onJoin`
  // and the client updates this via `setPlayerId` before the first
  // snapshot reaches the HUD. Local mode keeps the constructor's
  // PLAYER_ZERO default.
  private playerId: PlayerId;
  private readonly human: HumanCommandSource | null;
  private readonly getRouter: () => InputRouter | null;
  private readonly buildMode: BuildMode | null;

  constructor(opts?: {
    playerId?: PlayerId;
    human?: HumanCommandSource;
    selection?: Selection;
    getRouter?: () => InputRouter | null;
    buildMode?: BuildMode;
  }) {
    this.playerId = opts?.playerId ?? 0;
    this.human = opts?.human ?? null;
    this.getRouter = opts?.getRouter ?? (() => null);
    this.buildMode = opts?.buildMode ?? null;
    injectHudStyles();
    injectCommandCardStyles();
    injectHudCoherenceStyles();
    installHudTooltips();

    this.container = ensureDiv('hud', 'hud');

    this.resourceEl = document.createElement('div');
    this.resourceEl.className = 'hud__resources';
    const signalRow = makeStat('Signal', '—', 'hud-stat__primary');
    signalRow.row.querySelector('.hud-stat__label')?.classList.add('hud-stat__label--live');
    const incomeRow = makeStat('Income', '—', 'hud-stat__secondary');
    const squadsRow = makeStat('Squads', '—', 'hud-stat__secondary');
    // V1.7 — inline term tooltips. Hover (desktop only) reveals what each
    // line of the resource panel actually means. Touch is suppressed via
    // the (pointer:coarse) media query in hudStyles.
    signalRow.row.setAttribute(
      'data-tooltip',
      'Your resource. Earn it from captured Relays. Spend on squads and structures.',
    );
    incomeRow.row.setAttribute(
      'data-tooltip',
      'Signal per second. Each Relay you control adds to this — capture more to out-produce the enemy.',
    );
    squadsRow.row.setAttribute(
      'data-tooltip',
      'Current squads / cap, plus any squads currently in production. At the cap, production halts until a squad dies or you build a Production Hub.',
    );
    this.resourceSignalValue = signalRow.value;
    this.resourceIncomeValue = incomeRow.value;
    this.resourceSquadsValue = squadsRow.value;
    this.resourceEl.appendChild(signalRow.row);
    const signalBarTrack = document.createElement('div');
    signalBarTrack.className = 'hud-signal-bar';
    this.signalBarFill = document.createElement('div');
    this.signalBarFill.className = 'hud-signal-bar__fill';
    signalBarTrack.appendChild(this.signalBarFill);
    this.resourceEl.appendChild(signalBarTrack);
    this.resourceEl.appendChild(incomeRow.row);
    this.resourceEl.appendChild(squadsRow.row);

    // Underdog Surge indicator. Sits below the income row; CSS hides it when
    // not active. Visible regardless of which player is the viewer (we read
    // state.underdogSurgeActive[playerId]) so the HUD reflects the trailing
    // side's bonus from their own perspective. Bot side's surge state is
    // surfaced via the bot panel below so players can read enemy state.
    this.surgeIndicator = document.createElement('div');
    this.surgeIndicator.className = 'hud-surge';
    this.surgeIndicator.style.display = 'none';
    this.surgeIndicator.textContent = `SURGE +${(UNDERDOG_SURGE_BONUS * 100).toFixed(0)}%`;
    this.resourceEl.appendChild(this.surgeIndicator);

    // V1.5 Doctrine pill — sits below the resource line once the player has a
    // doctrine assigned. CSS hides it until then. One-second readability
    // (Rule 21): just the doctrine name, color-coded by id.
    this.doctrinePill = document.createElement('div');
    this.doctrinePill.className = 'hud-doctrine';
    this.doctrinePill.style.display = 'none';
    this.doctrinePill.setAttribute(
      'data-tooltip',
      'Your Specialization — picked mid-match. Gives a per-archetype bonus that shapes how you play the late game.',
    );
    this.resourceEl.appendChild(this.doctrinePill);
    this.container.appendChild(this.resourceEl);

    // Bot-side panel (top-right) so the human player can read the bot's
    // signal-side state. Currently surfaces the SURGE indicator only — a
    // SURGEing enemy is meaningful information the player should see (Rule 21
    // one-second readability). HUD never owns gameplay state; it reads
    // state.underdogSurgeActive[1 - playerId] each tick.
    this.enemyPanel = document.createElement('div');
    this.enemyPanel.className = 'hud-enemy';
    this.enemyPanel.style.display = 'none';
    this.enemyPanelTitle = document.createElement('div');
    this.enemyPanelTitle.className = 'hud-enemy__title';
    this.enemyPanel.appendChild(this.enemyPanelTitle);
    this.enemySurgeIndicator = document.createElement('div');
    this.enemySurgeIndicator.className = 'hud-surge hud-surge--enemy';
    this.enemySurgeIndicator.textContent = `SURGE +${(UNDERDOG_SURGE_BONUS * 100).toFixed(0)}%`;
    this.enemySurgeIndicator.style.display = 'none';
    this.enemyPanel.appendChild(this.enemySurgeIndicator);
    this.enemyDoctrinePill = document.createElement('div');
    this.enemyDoctrinePill.className = 'hud-doctrine hud-doctrine--enemy';
    this.enemyDoctrinePill.style.display = 'none';
    this.enemyPanel.appendChild(this.enemyDoctrinePill);
    this.container.appendChild(this.enemyPanel);

    // OVERTIME banner. Lives in the timer column so it replaces the regulation
    // dominance-check banner once OT begins. CSS uses a red palette + count-down
    // text for the remaining 60 s window.
    this.overtimeBanner = document.createElement('div');
    this.overtimeBanner.className = 'hud__overtime';
    this.overtimeBanner.style.display = 'none';
    this.container.appendChild(this.overtimeBanner);

    this.timerEl = document.createElement('div');
    this.timerEl.className = 'hud__timer';
    this.timerEl.textContent = '8:00';
    this.container.appendChild(this.timerEl);

    this.bannerEl = document.createElement('div');
    this.bannerEl.className = 'hud__banner';
    this.bannerEl.style.display = 'none';
    this.container.appendChild(this.bannerEl);

    this.contextEl = document.createElement('div');
    this.contextEl.className = 'hud__context';
    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'hud-context__empty';
    // V5+ — empty state hosts either a "Select All Squads (n)" CTA when the
    // player has own squads (mobile-RTS equivalent of desktop's box-drag), or
    // the original hint text when they have none yet. The text element is
    // wrapped so the button can replace it without reflowing the surrounding
    // panel anchor.
    this.emptyHint = document.createElement('div');
    this.emptyHint.className = 'hud-context__empty-hint';
    this.emptyHint.textContent = 'Select a squad, your Core, or a Hub';
    this.selectAllBtn = document.createElement('button');
    this.selectAllBtn.type = 'button';
    this.selectAllBtn.className = 'hud-context__select-all';
    this.selectAllBtn.style.display = 'none';
    this.selectAllBtn.addEventListener('click', () => {
      this.getRouter()?.triggerSelectAll();
    });
    this.emptyEl.appendChild(this.emptyHint);
    this.emptyEl.appendChild(this.selectAllBtn);
    this.contextEl.appendChild(this.emptyEl);

    this.squadPanel = document.createElement('div');
    this.squadPanel.className = 'hud-context__panel hud-context__panel--squad';
    // V4 — header with archetype portrait + squad name on one row. The
    // portrait is color-coded per archetype so the player reads the
    // selection at a glance without reading the title.
    const header = document.createElement('div');
    header.className = 'hud-context__header';
    this.squadPortrait = document.createElement('div');
    this.squadPortrait.className = 'hud-context__portrait';
    const nameStack = document.createElement('div');
    nameStack.className = 'hud-context__name-stack';
    this.squadName = document.createElement('div');
    this.squadName.className = 'hud-context__title';
    this.squadTagline = document.createElement('div');
    this.squadTagline.className = 'hud-context__tagline';
    nameStack.appendChild(this.squadName);
    nameStack.appendChild(this.squadTagline);
    header.appendChild(this.squadPortrait);
    header.appendChild(nameStack);
    this.squadPanel.appendChild(header);
    const hpRow = document.createElement('div');
    hpRow.className = 'hud-context__hp-row';
    const hpBar = document.createElement('div');
    hpBar.className = 'hud-context__hp-bar';
    this.squadHpFill = document.createElement('div');
    this.squadHpFill.className = 'hud-context__hp-fill';
    hpBar.appendChild(this.squadHpFill);
    this.squadHpText = document.createElement('div');
    this.squadHpText.className = 'hud-context__hp-text';
    hpRow.appendChild(hpBar);
    hpRow.appendChild(this.squadHpText);
    this.squadPanel.appendChild(hpRow);

    // V4 command grid — replaces the old two display-only ability rows with
    // four clickable cells: Attack-Move, Stop, Q-ability, E-ability. Click
    // parity with the keyboard hotkeys via InputRouter.trigger* hooks, so
    // desktop and mobile share one source of truth and players who don't
    // know the bindings can still command their squads.
    this.cmdGrid = document.createElement('div');
    this.cmdGrid.className = 'hud-context__commands';
    for (const spec of CMD_CELL_SPECS) {
      const cell = makeCmdCell(spec);
      cell.btn.addEventListener('click', () => this.onCmdClick(spec.key));
      this.cmdGrid.appendChild(cell.btn);
      this.cmdCells.set(spec.key, cell);
    }
    this.squadPanel.appendChild(this.cmdGrid);

    this.contextEl.appendChild(this.squadPanel);

    this.corePanel = document.createElement('div');
    this.corePanel.className = 'hud-context__panel hud-context__panel--core';
    this.coreTitle = document.createElement('div');
    this.coreTitle.className = 'hud-context__title';
    this.coreTitle.textContent = 'Core — Production';
    this.corePanel.appendChild(this.coreTitle);
    const coreStats = document.createElement('div');
    coreStats.className = 'hud-context__stats';
    this.coreSignal = document.createElement('span');
    this.coreSquads = document.createElement('span');
    coreStats.appendChild(this.coreSignal);
    coreStats.appendChild(this.coreSquads);
    this.corePanel.appendChild(coreStats);
    const buttonRow = document.createElement('div');
    buttonRow.className = 'hud-context__buttons';
    for (const archetype of PRODUCIBLE) {
      const def = SQUAD_DEFS[archetype];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hud-context__btn';
      btn.dataset.archetype = archetype;
      // V1.7 (2.11) — per-archetype purpose tooltip so players know WHY to
      // pick one over another at the moment of building, not after selecting
      // an already-built squad. HudTooltip's portal reads [data-tooltip].
      btn.setAttribute('data-tooltip', ARCHETYPE_BUILD_TOOLTIP[archetype]);
      const icon = document.createElement('div');
      icon.className = 'hud-context__btn-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = ARCHETYPE_GLYPH[archetype];
      const name = document.createElement('div');
      name.className = 'hud-context__btn-name';
      name.textContent = capitalize(archetype);
      const meta = document.createElement('div');
      meta.className = 'hud-context__btn-meta';
      meta.textContent = `${def.cost}S · ${def.buildTimeSec}s`;
      btn.appendChild(icon);
      btn.appendChild(name);
      btn.appendChild(meta);
      btn.addEventListener('click', () => this.onProduceClicked(archetype));
      buttonRow.appendChild(btn);
      this.buttons.set(archetype, { el: btn, meta });
    }
    this.corePanel.appendChild(buttonRow);
    this.queueText = document.createElement('div');
    this.queueText.className = 'hud-context__queue';
    this.corePanel.appendChild(this.queueText);

    const queueBar = document.createElement('div');
    queueBar.className = 'hud-context__queue-bar';
    this.queueBarFill = document.createElement('div');
    this.queueBarFill.className = 'hud-context__queue-bar-fill';
    queueBar.appendChild(this.queueBarFill);
    this.corePanel.appendChild(queueBar);

    // V4 in-card Build row — Hub / Turret buttons live inside the Core/Hub
    // command card so build actions are co-located with the producing
    // structure that hosts the slots. Clicking arms BuildMode the same way
    // the legacy bottom-center BuildPanel did, and InputRouter still
    // resolves the next world-click into a Build command.
    this.buildSection = document.createElement('div');
    this.buildSection.className = 'hud-context__build-section';
    const buildTitle = document.createElement('div');
    buildTitle.className = 'hud-context__build-title';
    buildTitle.textContent = 'BUILD (B)';
    this.buildSection.appendChild(buildTitle);
    const buildRow = document.createElement('div');
    buildRow.className = 'hud-context__build-row';
    for (const kind of PERMANENT_BUILDING_KINDS) {
      const def = BUILDINGS[kind];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hud-context__build-btn';
      btn.dataset.kind = kind;
      // V1.7 (2.11) — per-building purpose tooltip, same affordance as the
      // squad production tiles. Closes the "Hub vs Turret, which one?" gap.
      btn.setAttribute('data-tooltip', BUILD_KIND_TOOLTIP[kind]);
      const icon = document.createElement('div');
      icon.className = 'hud-context__btn-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = BUILD_KIND_GLYPH[kind];
      const name = document.createElement('div');
      name.className = 'hud-context__btn-name';
      name.textContent = labelForBuildKind(kind);
      const meta = document.createElement('div');
      meta.className = 'hud-context__btn-meta';
      meta.textContent = `${def.cost}S · ${def.buildTimeSec}s`;
      // V1.7 (3.18) — second meta line surfacing the EFFECT (not just the
      // cost/time). Hub: "+1 Queue", Turret: "30 DPS · 400px". The two-line
      // meta answers "which one and why" without requiring a hover; the
      // tooltip on the parent button keeps the long explanation.
      const effect = document.createElement('div');
      effect.className = 'hud-context__btn-effect';
      effect.textContent = BUILD_KIND_EFFECT[kind];
      btn.appendChild(icon);
      btn.appendChild(name);
      btn.appendChild(meta);
      btn.appendChild(effect);
      btn.addEventListener('click', () => this.onBuildClicked(kind));
      buildRow.appendChild(btn);
      this.buildButtons.set(kind, { el: btn, meta });
    }
    this.buildSection.appendChild(buildRow);
    // V1.7 (3.18) — footer hint surfaces the hidden third building type
    // (Engineer's Mini-Turret via Q). Without this, players see only the
    // two buttons here and conclude "two buildings, that's it" — the
    // playtest finding "building gameplay feels limited" maps cleanly to
    // the third option being completely invisible at decision time.
    const buildHint = document.createElement('div');
    buildHint.className = 'hud-context__build-hint';
    buildHint.textContent = BUILD_PANEL_FOOTER_HINT;
    this.buildSection.appendChild(buildHint);
    this.corePanel.appendChild(this.buildSection);
    this.contextEl.appendChild(this.corePanel);

    this.container.appendChild(this.contextEl);

    // React to build-mode arming changes so the in-card Build row reflects
    // armed state without waiting for the next snapshot.
    this.buildMode?.subscribe(() => this.renderContext());

    if (opts?.selection !== undefined) {
      opts.selection.subscribe((sel) => {
        this.latestSelection = sel;
        this.renderContext();
      });
    }
    this.renderContext();
  }

  // V2.5+ network mode: the server assigns the slot in `onJoin`. The
  // client receives the assignment over the `assigned` Colyseus
  // message and updates this HUD's playerId BEFORE the first
  // snapshot pump arrives. Re-renders the context panel so the
  // updated playerId is reflected in the next selection / state
  // change.
  setPlayerId(playerId: PlayerId): void {
    this.playerId = playerId;
    if (this.latestState !== null) this.update(this.latestState);
    this.renderContext();
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  show(): void {
    this.container.style.display = 'block';
  }

  update(state: MatchState): void {
    this.latestState = state;
    const player = state.players[this.playerId];
    this.resourceSignalValue.textContent = `${Math.floor(player.signal)}`;
    this.signalBarFill.style.width = `${(Math.min(1, player.signal / SQUAD_DEFS.shield.cost) * 100).toFixed(1)}%`;
    // V1.7 — append owned relay count to the income value so the player sees
    // the relay→income link in one read ("+11.0/s · 3 relays"). The tooltip
    // already explains *why* — the inline count makes the link visible at a
    // glance without a hover.
    let relays = 0;
    for (const n of Object.values(state.controlNodes)) {
      if (n.ownerId === this.playerId) relays += 1;
    }
    const relayLabel = relays === 1 ? '1 relay' : `${relays} relays`;
    this.resourceIncomeValue.textContent = `+${player.signalIncomePerSec.toFixed(1)}/s · ${relayLabel}`;
    // V1.7 (3.17): show how much production is in flight without making the
    // player click into the Core. Total queue across every producer the
    // viewer owns (Core + operational Hubs); +N appears only when the queue
    // is non-empty. At-cap state (count + queue ≥ cap → production stalls
    // until a squad dies) flips the row to the urgent treatment so the
    // player notices the squad cap was the reason their build stopped.
    let queuedAcrossProducers = 0;
    for (const b of Object.values(state.buildings)) {
      if (b.ownerId !== this.playerId) continue;
      if (b.kind !== 'production_hub') continue;
      if (!isOperational(b)) continue;
      queuedAcrossProducers += b.produces.queue.length;
    }
    for (const c of Object.values(state.cores)) {
      if (c.ownerId !== this.playerId) continue;
      if (c.produces === null) continue;
      queuedAcrossProducers += c.produces.queue.length;
    }
    const queueSuffix = queuedAcrossProducers > 0 ? ` · +${queuedAcrossProducers} queued` : '';
    this.resourceSquadsValue.textContent = `${player.squadCount} / ${player.squadCap}${queueSuffix}`;
    const atCap = player.squadCount + queuedAcrossProducers >= player.squadCap;
    this.resourceSquadsValue.dataset.atCap = atCap ? 'true' : 'false';
    this.renderTimer(state);
    this.renderSurge(state);
    this.renderDoctrine(state);
    this.renderContext();
  }

  private renderTimer(state: MatchState): void {
    if (state.phase === 'overtime') {
      this.renderOvertimeTimer(state);
      return;
    }
    const remaining = Math.max(0, state.matchLengthSec - state.matchSeconds);
    this.timerEl.textContent = formatClock(remaining);
    const urgent = remaining <= TIMER_FLASH_THRESHOLD_SEC;
    this.timerEl.dataset.urgent = String(urgent);
    this.overtimeBanner.style.display = 'none';

    // Dominance-check banner appears in the final DOMINANCE_BANNER_LEAD_SEC + a bit
    // window before timer expiry (7:30 → end on the V0.4 default 8-minute match).
    if (
      remaining > 0 &&
      remaining <= DOMINANCE_BANNER_LEAD_SEC &&
      remaining > TIMER_FLASH_THRESHOLD_SEC
    ) {
      this.bannerEl.style.display = 'block';
      this.bannerEl.textContent = `Dominance Check in 0:${Math.ceil(
        remaining - TIMER_FLASH_THRESHOLD_SEC,
      )
        .toString()
        .padStart(2, '0')}`;
    } else if (remaining > 0 && remaining <= TIMER_FLASH_THRESHOLD_SEC) {
      this.bannerEl.style.display = 'block';
      this.bannerEl.textContent = 'DOMINANCE CHECK IMMINENT';
    } else {
      this.bannerEl.style.display = 'none';
    }
  }

  // OT view: top timer ticks down the 60 s OT window; dominance banner is
  // replaced by a red OVERTIME pill that names the leader (or "TIED") so the
  // player feels the §17 dominance-must-be-a-real-threat pressure.
  private renderOvertimeTimer(state: MatchState): void {
    const otStart = state.overtimeStartedAtSec ?? state.matchLengthSec;
    const remaining = Math.max(0, otStart + OVERTIME_DURATION_SEC - state.matchSeconds);
    this.timerEl.textContent = `OT ${formatClock(remaining)}`;
    this.timerEl.dataset.urgent = 'true';
    this.bannerEl.style.display = 'none';
    this.overtimeBanner.style.display = 'block';
    const lead = state.overtimeLeadPlayer;
    if (lead === null) {
      this.overtimeBanner.textContent = 'OVERTIME · TIED';
    } else {
      const leaderName = state.players[lead].name;
      const held = Math.max(0, state.overtimeLeadHeldSec).toFixed(1);
      this.overtimeBanner.textContent = `OVERTIME · ${leaderName} leading ${held}s`;
    }
  }

  private renderSurge(state: MatchState): void {
    const mySurge = state.underdogSurgeActive[this.playerId];
    this.surgeIndicator.style.display = mySurge ? 'block' : 'none';

    const enemyId: PlayerId = this.playerId === 0 ? 1 : 0;
    const enemySurge = state.underdogSurgeActive[enemyId];
    const enemyDoctrine = state.players[enemyId].doctrineId;
    // Enemy panel is shown when there's anything to surface (surge OR doctrine).
    // The two indicators are independent so both can co-occur.
    if (enemySurge || enemyDoctrine !== null) {
      this.enemyPanel.style.display = 'block';
      this.enemyPanelTitle.textContent = state.players[enemyId].name;
    } else {
      this.enemyPanel.style.display = 'none';
    }
    this.enemySurgeIndicator.style.display = enemySurge ? 'block' : 'none';
  }

  private renderDoctrine(state: MatchState): void {
    const me = state.players[this.playerId];
    if (me.doctrineId === null) {
      this.doctrinePill.style.display = 'none';
    } else {
      this.doctrinePill.style.display = 'block';
      this.doctrinePill.dataset.doctrine = me.doctrineId;
      this.doctrinePill.textContent = `Doctrine: ${DOCTRINE_DEFS[me.doctrineId].name}`;
    }
    const enemyId: PlayerId = this.playerId === 0 ? 1 : 0;
    const enemyDoctrine = state.players[enemyId].doctrineId;
    if (enemyDoctrine === null) {
      this.enemyDoctrinePill.style.display = 'none';
    } else {
      this.enemyDoctrinePill.style.display = 'block';
      this.enemyDoctrinePill.dataset.doctrine = enemyDoctrine;
      this.enemyDoctrinePill.textContent = `Doctrine: ${DOCTRINE_DEFS[enemyDoctrine].name}`;
    }
  }

  private onProduceClicked(archetype: SquadArchetype): void {
    if (this.human === null) return;
    const sel = this.latestSelection;
    if (sel === null) return;
    if (sel.kind !== 'core' && sel.kind !== 'building') return;
    this.human.requestProduceSquad(sel.id, archetype);
  }

  private renderContext(): void {
    const sel = this.latestSelection;
    const state = this.latestState;
    if (sel === null || state === null) {
      this.show2('empty');
      return;
    }
    if (sel.kind === 'squad') {
      const squad: Squad | undefined = state.squads[sel.id];
      if (squad === undefined) {
        this.show2('empty');
        return;
      }
      this.renderSquad(squad);
      return;
    }
    if (sel.kind === 'core') {
      const core: Core | undefined = state.cores[sel.id];
      if (core === undefined || core.ownerId !== this.playerId) {
        this.show2('empty');
        return;
      }
      this.renderCore(core, state);
      return;
    }
    const building: Building | undefined = state.buildings[sel.id];
    if (building === undefined || building.ownerId !== this.playerId) {
      this.show2('empty');
      return;
    }
    this.renderBuilding(building, state);
  }

  private renderSquad(squad: Squad): void {
    this.show2('squad');
    const ratio = squad.maxHp > 0 ? Math.max(0, Math.min(1, squad.hp / squad.maxHp)) : 0;
    this.squadName.textContent = `${capitalize(squad.archetype)} squad`;
    this.squadTagline.textContent = ARCHETYPE_TAGLINE[squad.archetype];
    this.squadPortrait.dataset.archetype = squad.archetype;
    this.squadPortrait.textContent = ARCHETYPE_GLYPH[squad.archetype];
    this.squadHpFill.style.width = `${(ratio * 100).toFixed(1)}%`;
    this.squadHpFill.dataset.low = ratio < 0.4 ? 'true' : 'false';
    this.squadHpText.textContent = `${Math.ceil(squad.hp)} / ${squad.maxHp}`;
    this.renderCommandGrid(squad);
  }

  // V4 — fill the four command-grid cells for the selected squad. Click
  // wiring is parity with the keyboard handlers (InputRouter.trigger*) so
  // desktop and mobile share one source of truth. Per-cell state is
  // computed once in computeSquadCmdStates and applied here.
  private renderCommandGrid(squad: Squad): void {
    const router = this.getRouter();
    const armed = router === null ? 'idle' : router.getArmedMode();
    const states = computeSquadCmdStates(squad, this.playerId, armed);
    for (const [key, s] of states) {
      const cell = this.cmdCells.get(key);
      if (cell !== undefined) applyCmd(cell, s);
    }
  }

  private onCmdClick(key: CmdKey): void {
    const router = this.getRouter();
    if (router === null) return;
    // Clear ALL existing armed verbs first so only the one the player just
    // clicked is active. Build mode in particular takes priority over squad
    // commands in dispatchPrimaryClick, so leaving it armed would route the
    // next world-click to a build placement instead of the chosen ability.
    // Q/E on requiresTarget abilities arm the respective picker; targetless
    // abilities resolve immediately.
    router.triggerCancel();
    if (key === 'ability') router.triggerPrimaryAbility();
    else if (key === 'ability2') router.triggerSecondaryAbility();
  }

  private onBuildClicked(kind: PermanentBuildingKind): void {
    // Cancel any other armed verb (attack-move, mark-target, cut-link, or a
    // prior build-mode kind) so only the freshly clicked kind is armed.
    // triggerCancel clears every armed flag including buildMode; we then
    // re-arm the desired kind. Re-clicking the same kind toggles off
    // through BuildMode.arm's own same-kind branch.
    const router = this.getRouter();
    const wasArmed = this.buildMode?.getActive() === kind;
    router?.triggerCancel();
    if (!wasArmed) this.buildMode?.arm(kind);
  }

  private renderCore(core: Core, state: MatchState): void {
    // V1.7 (2.12 design fix): Hubs ADD a parallel queue rather than taking
    // over the Core's. The Core always produces while it's alive — the
    // previous "Hub Active" disabled-state branch is gone.
    const player = state.players[this.playerId];
    this.show2('core');
    this.coreTitle.textContent = 'Core — Production';
    this.coreTitle.setAttribute(
      'data-tooltip',
      'Your Core. If destroyed, you lose. Always produces squads — build Production Hubs for additional parallel queues.',
    );
    this.coreSignal.textContent = `Signal: ${Math.floor(player.signal)}`;
    this.coreSquads.textContent = `Squads: ${player.squadCount} / ${player.squadCap}`;
    const slot = core.produces;
    this.renderQueueText(slot);
    for (const archetype of PRODUCIBLE) {
      const ref = this.buttons.get(archetype);
      if (ref === undefined) continue;
      const def = SQUAD_DEFS[archetype];
      const queueLength = slot?.queue.length ?? 0;
      const blockedSlot = slot === null;
      const blockedCap = player.squadCount + queueLength >= player.squadCap;
      const blockedCost = player.signal < def.cost;
      ref.el.disabled = blockedSlot || blockedCap || blockedCost;
      ref.el.title = blockedSlot
        ? 'Production unavailable'
        : blockedCap
          ? 'Squad cap reached'
          : blockedCost
            ? 'Not enough Signal'
            : '';
    }
    this.renderBuildButtons(state);
  }

  private renderBuilding(building: Building, state: MatchState): void {
    if (building.kind !== 'production_hub') {
      // Turret panel is read-only. Hide build row — you don't build from a turret.
      this.show2('core');
      this.coreTitle.textContent = isOperational(building) ? 'Turret' : 'Turret (constructing)';
      this.coreTitle.setAttribute(
        'data-tooltip',
        'Static defense. Fires at the nearest enemy squad. Place near choke points or your Core.',
      );
      this.coreSignal.textContent = `HP: ${Math.ceil(building.hp)} / ${building.maxHp}`;
      this.coreSquads.textContent = isOperational(building)
        ? 'Targeting nearest enemy squad'
        : `Build ${(building.buildProgress * 100).toFixed(0)}%`;
      this.queueText.textContent = '';
      this.disableAllProd('Turrets do not produce squads');
      this.buildSection.style.display = 'none';
      return;
    }
    this.show2('core');
    const hub: ProductionHub = building;
    const player = state.players[this.playerId];
    if (!isOperational(hub)) {
      this.coreTitle.textContent = 'Production Hub (constructing)';
      this.coreTitle.setAttribute(
        'data-tooltip',
        'Production Hub. Adds a parallel squad production queue when complete — your Core keeps producing alongside it.',
      );
      this.coreSignal.textContent = `Build ${(hub.buildProgress * 100).toFixed(0)}%`;
      this.coreSquads.textContent = `HP: ${Math.ceil(hub.hp)} / ${hub.maxHp}`;
      this.queueText.textContent = 'Hub will accept squads when complete.';
      this.disableAllProd('Hub still under construction');
      this.renderBuildButtons(state);
      return;
    }
    this.coreTitle.textContent = 'Production Hub';
    this.coreTitle.setAttribute(
      'data-tooltip',
      'Production Hub. Adds a parallel squad production queue — your Core produces alongside this Hub.',
    );
    this.coreSignal.textContent = `Signal: ${Math.floor(player.signal)}`;
    this.coreSquads.textContent = `Squads: ${player.squadCount} / ${player.squadCap}`;
    const slot = hub.produces;
    this.renderQueueText(slot);
    for (const archetype of PRODUCIBLE) {
      const ref = this.buttons.get(archetype);
      if (ref === undefined) continue;
      const def = SQUAD_DEFS[archetype];
      const queueLength = slot.queue.length;
      const blockedCap = player.squadCount + queueLength >= player.squadCap;
      const blockedCost = player.signal < def.cost;
      ref.el.disabled = blockedCap || blockedCost;
      ref.el.title = blockedCap
        ? 'Squad cap reached'
        : blockedCost
          ? 'Not enough Signal'
          : '';
    }
    this.renderBuildButtons(state);
  }

  private renderQueueText(slot: { queue: SquadArchetype[]; progress: number } | null): void {
    if (slot === null) {
      this.queueText.textContent = 'Production unavailable.';
      this.queueBarFill.style.width = '0%';
      this.queueBarFill.dataset.active = 'false';
      this.queueBarFill.dataset.archetype = '';
    } else if (slot.queue.length === 0) {
      this.queueText.textContent = 'Queue: empty';
      this.queueBarFill.style.width = '0%';
      this.queueBarFill.dataset.active = 'false';
      this.queueBarFill.dataset.archetype = '';
    } else {
      const head = slot.queue[0] ?? 'recon';
      const headDef = SQUAD_DEFS[head];
      const remainSec = Math.max(0, headDef.buildTimeSec * (1 - slot.progress)).toFixed(1);
      const tail = slot.queue.slice(1).map(capitalize).join(', ');
      this.queueText.textContent =
        `${capitalize(head)} (${remainSec}s)` + (tail.length > 0 ? ` · ${tail}` : '');
      this.queueBarFill.style.width = `${(slot.progress * 100).toFixed(1)}%`;
      this.queueBarFill.dataset.active = 'true';
      this.queueBarFill.dataset.archetype = head;
    }
  }

  private disableAllProd(title: string): void {
    for (const archetype of PRODUCIBLE) {
      const ref = this.buttons.get(archetype);
      if (ref === undefined) continue;
      ref.el.disabled = true;
      ref.el.title = title;
    }
  }

  // V4 — drives the in-card Build row (Hub / Turret). Affordability is
  // computed by computeBuildKindStates (shared with BuildPanel's own logic)
  // so the two surfaces always agree. Armed state highlights the active
  // kind so the player sees that their next world-click will place it.
  private renderBuildButtons(state: MatchState): void {
    this.buildSection.style.display = '';
    const states = computeBuildKindStates(state, this.playerId);
    const armedKind = this.buildMode?.getActive() ?? null;
    for (const [kind, ref] of this.buildButtons) {
      const s = states.get(kind);
      if (s === undefined) continue;
      ref.el.disabled = s.disabled;
      ref.el.dataset.armed = String(armedKind === kind);
      ref.el.title = s.title;
    }
  }

  private show2(which: 'empty' | 'squad' | 'core'): void {
    this.emptyEl.style.display = which === 'empty' ? 'block' : 'none';
    this.squadPanel.style.display = which === 'squad' ? 'block' : 'none';
    this.corePanel.style.display = which === 'core' ? 'block' : 'none';
    if (which === 'empty') this.renderEmptyState();
  }

  // V5+ — when the player has 2+ own squads, the empty hint becomes a
  // "Select All Squads (n)" button (mobile-RTS equivalent of desktop's
  // box-drag, and a useful shortcut for desktop players too). When they have
  // 0–1 squads the button has no value (1 squad is already trivially
  // selectable via tap), so the original hint text is shown instead.
  private renderEmptyState(): void {
    const state = this.latestState;
    let ownSquads = 0;
    if (state !== null) {
      for (const s of Object.values(state.squads)) {
        if (s.ownerId === this.playerId) ownSquads += 1;
      }
    }
    if (ownSquads >= 2) {
      this.emptyHint.style.display = 'none';
      this.selectAllBtn.style.display = '';
      this.selectAllBtn.textContent = `⊟ Select All Squads (${ownSquads})`;
    } else {
      this.emptyHint.style.display = '';
      this.selectAllBtn.style.display = 'none';
    }
  }
}

